import type {
	ModuleProps,
	T2Schema,
	TranslationKey,
} from "@typedefs/modulesInterfaces";
import type {
	FloodedMinorSeasonCreatePayload,
	FloodedMinorSeasonData,
	ModuleFull,
} from "./floodedRiceTypes";
import GenericOptionalSubModule from "../GenericOptionalSubmodule";
import GenericModule from "../GenericModule";
import { memo, useEffect, useMemo } from "react";
import { useAppSelector, usePermissions } from "@app/hooks";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetFloodedRiceMinorSeasons,
	useCreateFloodedRiceMinorSeasonMutation,
	useDeleteFloodedRiceMinorSeasonMutation,
} from "@hooks/index";
import { FEModules } from "@utils/moduleList";
import { useContextAwareGetLandUseChange } from "@hooks/index";
import { mergeLocalAndServerChanges } from "../inputs/utils";
import { useFormikContext } from "formik";
import { isEqual } from 'lodash';

interface FloodedRiceTierOneProps {
	initValues: ModuleFull;
	t2Schema: T2Schema;
	setInitValues: React.Dispatch<React.SetStateAction<ModuleFull>>;
}

// Configuration object for flooded rice seasons
const FLOODED_RICE_CONFIG = {
	seasons: {
		fieldName: "seasons" as const,
		translationKey: "module.add_new_season" as const,
		payloadType: "FloodedMinorSeasonCreatePayload" as const,
	},
} as const;

const FloodedRiceTierOne = ({
	moduleSchema,
	initValues,
	title,
	t2Schema,
	isLoading,
	beModuleType,
	blockingError,
	footerError,
	isError,
	setInitValues,
}: ModuleProps & FloodedRiceTierOneProps) => {
	const moduleProps = {
		title,
		moduleSchema,
		footerError,
		blockingError,
		isError,
		isLoading,
	};

	const MAX_SEASONS = 4;

	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const { isReadOnly } = usePermissions();

	// Mutation hooks
	const [deleteSeason, { isLoading: isLoadingDeleteSeason }] = useDeleteFloodedRiceMinorSeasonMutation();
	const [addSeason, { isLoading: isLoadingAddSeason }] = useCreateFloodedRiceMinorSeasonMutation();

	const lucId = moduleList.find((module) => module.id === FEModules.LandUseChange)?.uniqueId;
	const parentId = moduleList.find((m) => m.id === FEModules.FloodedRice)?.uniqueId;

	// Generic remove handler
	const createRemoveHandler = () => (
		remove: <T>(index: number) => T | undefined,
		index: number,
		id?: number | null,
	) => {
		remove(index);

		if (id) {
			if (deleteSeason) {
				deleteSeason({
					id,
					activityId: activeActivityId ?? 0,
					parent: parentId ?? 0,
				});
			}
		}
	};

	// Generic add handler
	const createAddHandler = () => async () => {
		const payload: FloodedMinorSeasonCreatePayload = {
			body: { parent: parentId ?? 0 },
			activityId: activeActivityId ?? 0,
		};

		try {
			if (addSeason && initValues.seasons.length < MAX_SEASONS) {
				await addSeason(payload).unwrap();
			}
		} catch (err) {
			console.error(err);
		}
	};

	const translationKeySubmodule = (name: string): TranslationKey => {
		const config = FLOODED_RICE_CONFIG[name as keyof typeof FLOODED_RICE_CONFIG];
		return config ? config.translationKey : "module.add_new_season"; // fallback
	};

	/* ADD SUBMODULES LOGIC */
	const { values } = useFormikContext<ModuleFull>();
	const { data: seasonsData, isLoading: isLoadingSeasons } = useContextAwareGetFloodedRiceMinorSeasons(
		{ activityId: activeActivityId ?? 0 },
		{
			refetchOnMountOrArgChange: true,
		},
	);
	const { data: lucData } = useContextAwareGetLandUseChange(lucId ?? 0, { refetchOnMountOrArgChange: true, skip: !lucId || lucId === 0 });

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	const lists = useMemo(() => {
		const isSeasonsUpdated = !isEqual(values.seasons, seasonsData);
		const isLucUpdated = !isEqual(lucData, values.seasons.map((season) => season.land_use_change));

		const seasonsWithLuc: FloodedMinorSeasonData[] | undefined = seasonsData?.map((season) => ({
			...season,
			land_use_change: lucData?.module,
		})) as FloodedMinorSeasonData[];

		return {
			seasons: isSeasonsUpdated || isLucUpdated
				? mergeLocalAndServerChanges({
					currentChangesList: values.seasons.map((season) => ({ ...season, land_use_change: lucData?.module })) as FloodedMinorSeasonData[],
					incomingList: seasonsWithLuc ?? [],
				}) : values.seasons
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [seasonsData, lucData]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (lists.seasons) {
			setInitValues(() => ({
				...values,
				seasons: lists.seasons,
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lists, setInitValues]);

	return (
		<GenericModule
			{...moduleProps}
			t2Schema={t2Schema}
			beModuleType={beModuleType}
			disableOptionals
		>
			{
				({ belongsToLuc, isSWW }) => (
					<>
						{moduleSchema.optionalSubmodules?.map((subModule, index) => {
							return (
								<GenericOptionalSubModule<FloodedMinorSeasonData>
									key={`${subModule.name}-${index}`}
									optionalSchema={subModule}
									values={initValues.seasons}
									createHandler={createAddHandler()}
									removeHandler={createRemoveHandler()}
									fieldArrayName={subModule.name}
									addLabel={translationKeySubmodule(subModule.name)}
									belongsToLuc={belongsToLuc}
									isSWW={isSWW}
									isReadOnly={isReadOnly}
									isOptionalLoading={isLoadingSeasons}
									isCreateLoading={isLoadingAddSeason}
									isDeleteLoading={isLoadingDeleteSeason}
								/>
							);
						})}
					</>
				)
			}
		</GenericModule>
	);
};

export default memo(FloodedRiceTierOne);
